/**
 * Team → Dropped tasks — prototype `dropped` (docs/prototype.html 2373): everything someone chose not to do,
 * with the reason, so nothing is lost quietly. GET /dropped (every page, newest first). Restore →
 * POST /dropped/{id}/restore (a new task for the original person); Seen / Mark all as seen →
 * POST /dropped/seen. Source and person filters work on the loaded list.
 */
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { DROP_REASON_LABEL, DROPPED_SOURCE_LABEL, type DroppedItem, type DropReason } from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../../api';
import { firstName } from '../../lib/format';
import { useDropped, useWorkWrite } from '../../lib/work';
import { Avatar, Empty, ErrorState, Loading, MultiSelectFilter, errorText, useToast } from '../../ui/kit';
import { panelSearch } from '../requests/ItemPanels';

type Src = DroppedItem['source_kind'];
const SOURCES = Object.entries(DROPPED_SOURCE_LABEL) as [Src, string][];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** "4 Oct, 17:40" in India time. */
function whenLabel(ts: string): string {
  const d = new Date(ts);
  const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' }).format(d);
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' }).format(d);
  return `${day}, ${time}`;
}

const UUID_IN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
/**
 * "Came from", in plain words — never a bare id. A declined Sales/Marketing request is stored as
 * "<Team> request <uuid>" (API requests.ts); it reads "Declined Sales request" and opens that request's panel.
 */
function cameFrom(d: DroppedItem): { text: string; requestId?: string } | null {
  const ref = d.source_ref?.trim();
  if (!ref) return null;
  if (d.source_kind === 'task' && UUID.test(ref)) return { text: 'Dropped from the task list' };
  const req = /^(.+?) request ([0-9a-f-]{36})$/i.exec(ref);
  if (d.source_kind === 'request' && req && UUID.test(req[2]!)) return { text: `Declined ${req[1]} request`, requestId: req[2]! };
  const plain = ref.replace(UUID_IN, '').replace(/\s{2,}/g, ' ').trim();
  return plain ? { text: plain } : null;
}

export function Dropped() {
  const q = useDropped();
  const loc = useLocation();
  const toast = useToast();
  const [src, setSrc] = useState<Src | 'all'>('all');
  // Who dropped it, any number of people; empty = everyone. useDropped loads every page, so this filters the whole list.
  const [who, setWho] = useState<string[]>([]);

  const items = useMemo(() => q.data ?? [], [q.data]);
  const people = useMemo(() => {
    const m = new Map<string, DroppedItem['by']>();
    for (const d of items) m.set(d.by.id, d.by);
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);
  const shown = items.filter((d) => (src === 'all' || d.source_kind === src) && (!who.length || who.includes(d.by.id)));

  const open = items.filter((d) => d.state === 'open');
  const unseen = open.filter((d) => !d.seen_by_manager);
  const summary = useMemo(() => {
    if (!items.length) return null;
    const by = new Map<string, { name: string; n: number }>();
    const why = new Map<DropReason, number>();
    for (const d of items) {
      const b = by.get(d.by.id) ?? { name: d.by.name, n: 0 };
      b.n++;
      by.set(d.by.id, b);
      why.set(d.reason_code, (why.get(d.reason_code) ?? 0) + 1);
    }
    const top = [...by.values()].sort((a, b) => b.n - a.n)[0]!;
    const reason = [...why].sort((a, b) => b[1] - a[1])[0]![0];
    return { top, reason };
  }, [items]);

  const restore = useWorkWrite((v: { d: DroppedItem; key: string }) => call('dropped.restore', { params: { id: v.d.id }, idempotencyKey: v.key, body: {} }));
  const seen = useWorkWrite((ids: string[]) => call('dropped.markSeen', { body: { ids } }));
  const markSeen = (ids: string[], msg: string) =>
    seen.mutate(ids, { onSuccess: () => toast(msg), onError: (e) => toast(errorText(e)) });

  if (q.isLoading)
    return (
      <div className="panel">
        <Loading />
      </div>
    );
  if (q.error)
    return (
      <div className="panel">
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );

  return (
    <>
      <div className="ph" style={{ margin: '0 0 12px', justifyContent: 'flex-end' }}>
        <div className="r" style={{ marginLeft: 0 }}>
          <button className="btn" disabled={!unseen.length || seen.isPending} onClick={() => markSeen(unseen.slice(0, 200).map((d) => d.id), 'Marked as seen')}>
            Mark all as seen
          </button>
        </div>
      </div>
      <div className="note" style={{ marginBottom: 14 }}>
        {summary ? (
          <>
            <b>{items.length} dropped.</b> {unseen.length ? `${unseen.length} you have not looked at yet.` : open.length ? 'You have looked at all of them.' : 'All of them are restored.'} Most by {firstName(summary.top.name)} ({summary.top.n}). Most common reason:{' '}
            {DROP_REASON_LABEL[summary.reason].toLowerCase()}.
          </>
        ) : (
          <>
            <b>Nothing dropped.</b> When someone leaves a task out, it shows here with the reason.
          </>
        )}
      </div>

      <div className="bar">
        <button className={`fp${src === 'all' ? ' on' : ''}`} onClick={() => setSrc('all')} aria-pressed={src === 'all'}>
          All sources
        </button>
        {SOURCES.map(([k, l]) => (
          <button key={k} className={`fp${src === k ? ' on' : ''}`} onClick={() => setSrc(k)} aria-pressed={src === k}>
            {l}
          </button>
        ))}
        <span className="grow" />
        <MultiSelectFilter
          label="Dropped by"
          allLabel="Everyone"
          options={people.map((p) => ({ value: p.id, label: p.name, count: items.filter((d) => d.by.id === p.id).length }))}
          value={who}
          onChange={setWho}
        />
      </div>

      <div className="panel">
        {!shown.length ? (
          <Empty title="Nothing here">{items.length ? 'Switch the filter above.' : 'Nothing has been dropped.'}</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Task</th>
                <th>Came from</th>
                <th>Left out by</th>
                <th>Why</th>
                <th>When</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((d) => {
                const from = cameFrom(d);
                return (
                  <tr key={d.id}>
                    <td>
                      <b>{d.title}</b> {d.state === 'open' && !d.seen_by_manager ? <span className="chip brand">New</span> : null}
                      <div className="sub">
                        {d.client ? (
                          <>
                            <Link to={`/clients/book?client=${d.client.id}`} style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}>
                              {d.client.name}
                            </Link>{' '}
                            {d.client.priority === 'high' ? (
                              <span className="pm" title="High priority client">
                                ▲
                              </span>
                            ) : null}
                          </>
                        ) : (
                          'Internal'
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="chip info">{DROPPED_SOURCE_LABEL[d.source_kind]}</span>
                      {from ? (
                        <div className="sub">
                          {from.requestId ? (
                            <Link to={`${loc.pathname}${panelSearch(loc.search, 'internal_request', from.requestId)}`} style={{ color: 'var(--brand)', fontWeight: 600, textDecoration: 'none' }}>
                              {from.text}
                            </Link>
                          ) : (
                            from.text
                          )}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Avatar user={d.by} size={26} /> {firstName(d.by.name)}
                      </span>
                    </td>
                    <td>
                      <span className={`chip ${d.reason_code === 'duplicate' ? '' : 'warm'}`}>{DROP_REASON_LABEL[d.reason_code]}</span>
                      {d.note ? <div className="sub">{d.note}</div> : null}
                    </td>
                    <td className="sub" style={{ color: 'var(--sub)' }}>
                      {whenLabel(d.at)}
                    </td>
                    <td className="tr">
                      {d.state === 'restored' ? (
                        <>
                          <span className="chip hot">Restored</span>
                          {d.restored_task_id ? (
                            <div>
                              <Link className="sub" to={`/work/allwork?task=${d.restored_task_id}`}>
                                Open the task
                              </Link>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <button
                            className="btn sm good"
                            disabled={restore.isPending}
                            onClick={() =>
                              restore.mutate(
                                { d, key: newIdempotencyKey() },
                                {
                                  onSuccess: (t) => toast(`Restored. It is back on ${t.assignee ? firstName(t.assignee.name) + '’s' : 'the'} list.`),
                                  onError: (e) => toast(errorText(e)),
                                },
                              )
                            }
                          >
                            Restore
                          </button>
                          {!d.seen_by_manager ? (
                            <button className="btn sm ghost" disabled={seen.isPending} onClick={() => markSeen([d.id], 'Marked as seen')}>
                              Seen
                            </button>
                          ) : null}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
